import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Clock, RefreshCw, LogOut } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// JWT access tokens live for 30 min. Warn the user 3 min before expiry.
const WARN_BEFORE_EXPIRY_MS = 3 * 60 * 1000;
const CHECK_INTERVAL_MS = 15 * 1000;

function decodeJwtExp(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export default function SessionTimeoutWarning({ onLogout }) {
  const [visible, setVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [extending, setExtending] = useState(false);
  const tickRef = useRef(null);

  const refreshToken = useCallback(async () => {
    setExtending(true);
    try {
      const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      if (data?.token) localStorage.setItem('admin_token', data.token);
      setVisible(false);
    } catch {
      onLogout && onLogout();
    }
    setExtending(false);
  }, [onLogout]);

  useEffect(() => {
    const check = () => {
      const token = localStorage.getItem('admin_token');
      if (!token) { setVisible(false); return; }
      const exp = decodeJwtExp(token);
      if (!exp) { setVisible(false); return; }
      const msLeft = exp - Date.now();
      if (msLeft <= 0) {
        setVisible(false);
        onLogout && onLogout();
        return;
      }
      if (msLeft <= WARN_BEFORE_EXPIRY_MS) {
        setVisible(true);
        setSecondsLeft(Math.ceil(msLeft / 1000));
      } else {
        setVisible(false);
      }
    };
    check();
    tickRef.current = setInterval(check, CHECK_INTERVAL_MS);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [onLogout]);

  // Countdown update every second while visible
  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => {
      const token = localStorage.getItem('admin_token');
      const exp = token && decodeJwtExp(token);
      if (!exp) return;
      const msLeft = exp - Date.now();
      if (msLeft <= 0) {
        setVisible(false);
        onLogout && onLogout();
      } else {
        setSecondsLeft(Math.ceil(msLeft / 1000));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [visible, onLogout]);

  if (!visible) return null;

  const mm = Math.floor(secondsLeft / 60).toString().padStart(2, '0');
  const ss = (secondsLeft % 60).toString().padStart(2, '0');

  return (
    <div
      data-testid="session-timeout-warning"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 12, padding: '1.5rem 1.75rem', width: '92%', maxWidth: 440,
        boxShadow: '0 20px 60px rgba(0,0,0,0.18)', border: '1px solid #fde68a',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ background: '#fef3c7', color: '#b45309', borderRadius: 999, padding: 8 }}>
            <Clock size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>Session Expiring Soon</h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#64748b' }}>For your security, you'll be signed out automatically.</p>
          </div>
        </div>

        <div style={{
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
          padding: '0.85rem 1rem', marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: '0.8rem', color: '#92400e' }}>Time remaining</span>
          <span data-testid="session-countdown" style={{ fontSize: '1.3rem', fontWeight: 700, color: '#b45309', fontVariantNumeric: 'tabular-nums' }}>
            {mm}:{ss}
          </span>
        </div>

        <p style={{ fontSize: '0.8rem', color: '#475569', margin: '0.85rem 0 1rem' }}>
          Extend your session to keep working, or sign out to end it now.
        </p>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            data-testid="session-logout-btn"
            onClick={() => { setVisible(false); onLogout && onLogout(); }}
            disabled={extending}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '0.55rem 0.9rem',
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
              color: '#475569', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer',
            }}
          >
            <LogOut size={14} /> Sign out
          </button>
          <button
            data-testid="session-extend-btn"
            onClick={refreshToken}
            disabled={extending}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '0.55rem 1rem',
              background: '#3b82f6', border: 'none', borderRadius: 8,
              color: '#fff', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
              opacity: extending ? 0.6 : 1,
            }}
          >
            <RefreshCw size={14} className={extending ? 'animate-spin' : ''} />
            {extending ? 'Extending…' : 'Extend session'}
          </button>
        </div>
      </div>
    </div>
  );
}
